import React, { useState } from 'react'
import { Button, SegmentSwitch } from '@instacart/tds'
import { ActionFlow, type ActionOutcome } from '~shared/lib/ActionFlow'
import { LocalSettings } from '../../Types/Enums'
import styles from './KdsSettings.module.scss'

type KdsSettingsProps = {
  initialPrintingEnabled?: boolean
  initialAutomaticPrintingEnabled?: boolean
  onSettingsSave: (newSettings: LocalSettings) => void
  cancel: () => void
}

type PrintingMode = 'off' | 'manual' | 'automatic'

const printingModeItems = [
  { label: 'Off', value: 'off' },
  { label: 'Manual', value: 'manual' },
  { label: 'Automatic', value: 'automatic' },
] satisfies Array<{ label: string; value: PrintingMode }>

const getPrintingMode = ({
  printingEnabled,
  automaticPrintingEnabled,
}: LocalSettings): PrintingMode => {
  if (!printingEnabled) return 'off'
  return (automaticPrintingEnabled ?? true) ? 'automatic' : 'manual'
}

const getSettingsForPrintingMode = (printingMode: PrintingMode): LocalSettings => {
  switch (printingMode) {
    case 'off':
      return { printingEnabled: false, automaticPrintingEnabled: false }
    case 'manual':
      return { printingEnabled: true, automaticPrintingEnabled: false }
    case 'automatic':
      return { printingEnabled: true, automaticPrintingEnabled: true }
    default: {
      const _exhaustive: never = printingMode
      return _exhaustive
    }
  }
}

const parsePrintingMode = (value: string): PrintingMode => {
  if (value === 'off' || value === 'manual' || value === 'automatic') return value
  return 'off'
}

export const KdsSettings: React.FC<KdsSettingsProps> = props => {
  const { initialPrintingEnabled, initialAutomaticPrintingEnabled, onSettingsSave, cancel } = props
  const [printingMode, setPrintingMode] = useState<PrintingMode>(() =>
    getPrintingMode({
      printingEnabled: initialPrintingEnabled,
      automaticPrintingEnabled: initialAutomaticPrintingEnabled,
    })
  )

  const saveSettings = (): ActionOutcome => {
    const newLocalSettings = getSettingsForPrintingMode(printingMode)

    localStorage.setItem('departmentOrderSettings', JSON.stringify(newLocalSettings))
    onSettingsSave(newLocalSettings)
    return { type: 'success' }
  }

  return (
    <ActionFlow.Modal
      isOpen={true}
      onClose={cancel}
      initialPath="/settings"
      modalClassName={styles.settingsModal}
    >
      <ActionFlow.Page
        path="/settings"
        header="Settings"
        layout="inlineRight"
        footer={<SettingsFooter onSave={saveSettings} onCancel={cancel} />}
      >
        <div data-testid="kds-settings" className={styles.settingsControl}>
          <div className={styles.label}>Printing</div>
          <SegmentSwitch
            aria-label="Printing mode"
            items={printingModeItems}
            onChange={value => setPrintingMode(parsePrintingMode(value))}
            value={printingMode}
          />
        </div>
      </ActionFlow.Page>
    </ActionFlow.Modal>
  )
}

const SettingsFooter: React.FC<{
  onSave: () => ActionOutcome
  onCancel: () => void
}> = ({ onSave, onCancel }) => (
  <div className={styles.buttonGroup}>
    <Button.Group>
      <ActionFlow.Button action={onSave}>Save</ActionFlow.Button>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
    </Button.Group>
  </div>
)
